import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FileText, AlertCircle, Scale, Ban } from "lucide-react";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Logo } from "@/components/Logo";

const Terms = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - LoopBaan.ai</title>
        <meta name="description" content="LoopBaan.ai Terms of Service. Read our terms and conditions for using our AI-powered resume and cover letter service." />
        <link rel="canonical" href="https://loopbaan.ai/terms" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <MegaMenuNav />

        {/* Hero */}
        <section className="border-b bg-muted/30 py-16">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl text-center">
              <div className="mb-6 flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <Scale className="h-8 w-8 text-primary" />
                </div>
              </div>
              <h1 className="mb-4 text-4xl font-bold md:text-5xl">Terms of Service for LoopBaan.ai</h1>
              <p className="text-lg text-muted-foreground">
                Last Updated: October 25, 2025 | Effective Date: October 25, 2025
              </p>
              <p className="mt-4 text-sm text-muted-foreground max-w-3xl mx-auto">
                These Terms of Service (&quot;Terms&quot;) constitute a legally binding agreement between you and LoopBaan.ai LLC (&quot;LoopBaan,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) governing your access to and use of loopbaan.ai (the &quot;Service&quot;).
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl">
              
              {/* Important Notice */}
              <Card className="mb-12 border-destructive/30 bg-destructive/5 p-8">
                <div className="flex gap-4">
                  <AlertCircle className="h-8 w-8 flex-shrink-0 text-destructive" />
                  <div>
                    <h2 className="mb-2 text-xl font-bold text-destructive">Important Agreement</h2>
                    <p className="text-sm text-muted-foreground">
                      <strong>BY CREATING AN ACCOUNT OR USING OUR SERVICE, YOU AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE, DO NOT USE THE SERVICE.</strong>
                    </p>
                  </div>
                </div>
              </Card>

              {/* Quick Summary */}
              <Card className="mb-12 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 p-8">
                <h2 className="mb-6 text-2xl font-bold">Quick Summary</h2>
                <div className="grid gap-6 md:grid-cols-3">
                  {[
                    { icon: FileText, title: "You Own Your Content", desc: "All resumes and documents you create are yours to keep and use." },
                    { icon: AlertCircle, title: "Use Responsibly", desc: "Review all AI-generated content before use. No guarantees of job outcomes." },
                    { icon: Ban, title: "No Abuse", desc: "Don&apos;t use our service for illegal purposes or to harm others." }
                  ].map((item) => (
                    <div key={item.title} className="flex gap-3">
                      <item.icon className="h-6 w-6 flex-shrink-0 text-primary" />
                      <div>
                        <h3 className="mb-1 font-semibold">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Sections */}
              <div className="prose prose-slate max-w-none space-y-8">
                
                {/* Section 1 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">1. Acceptance of Terms</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">1.1 Agreement to Terms</h3>
                    <p className="text-muted-foreground">
                      By accessing, browsing, or using LoopBaan, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">1.2 Eligibility</h3>
                    <p className="mb-4 text-muted-foreground">
                      You must be at least 18 years old to use this Service. By using LoopBaan, you represent and warrant that:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• You are at least 18 years of age</li>
                      <li>• You have the legal capacity to enter into binding contracts</li>
                      <li>• Your use of the Service complies with all applicable laws and regulations</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">1.3 Modifications to Terms</h3>
                    <p className="text-muted-foreground">
                      We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the Service with a new &quot;Last Updated&quot; date. Your continued use of the Service after changes constitutes acceptance of the modified Terms. We will notify you of material changes via email or prominent notice on our website.
                    </p>
                  </Card>
                </div>

                {/* Section 2 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">2. Description of Service</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">2.1 What LoopBaan Provides</h3>
                    <p className="mb-4 text-muted-foreground">
                      LoopBaan is an AI-powered software platform that helps users create professional resumes and cover letters. Our Service uses artificial intelligence to:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Generate customized resume content based on user-provided information</li>
                      <li>• Create tailored cover letters for specific job applications</li>
                      <li>• Format and organize professional documents</li>
                      <li>• Provide templates and design options</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">2.2 Service Availability</h3>
                    <p className="mb-4 text-muted-foreground">
                      We strive to maintain 99.9% uptime, but we do not guarantee uninterrupted or error-free access. The Service may be unavailable due to:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Scheduled maintenance</li>
                      <li>• Technical issues</li>
                      <li>• Force majeure events</li>
                      <li>• Third-party service disruptions</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">2.3 Service Changes</h3>
                    <p className="text-muted-foreground">
                      We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice. We are not liable for any modification, suspension, or discontinuation.
                    </p>
                  </Card>
                </div>

                {/* Section 3 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">3. Account Registration and Security</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">3.1 Account Creation</h3>
                    <p className="mb-4 text-muted-foreground">
                      To use certain features of the Service, you must create an account by providing:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Valid email address</li>
                      <li>• Secure password</li>
                      <li>• Accurate personal information</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">3.2 Account Responsibilities</h3>
                    <p className="mb-4 text-muted-foreground">You are responsible for:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Maintaining the confidentiality of your account credentials</li>
                      <li>• All activities that occur under your account</li>
                      <li>• Notifying us immediately of any unauthorized access or security breach</li>
                      <li>• Ensuring your account information is accurate and up-to-date</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">3.3 Account Termination</h3>
                    <p className="mb-4 text-muted-foreground">
                      We reserve the right to suspend or terminate your account at any time, without prior notice, if:
                    </p>
                    <ul className="mb-4 space-y-2 text-muted-foreground">
                      <li>• You violate these Terms</li>
                      <li>• You engage in fraudulent or illegal activity</li>
                      <li>• You abuse or misuse the Service</li>
                      <li>• Your account has been inactive for 24+ months</li>
                      <li>• Required by law or legal process</li>
                    </ul>
                    <p className="text-muted-foreground">
                      You may terminate your account at any time through your account settings or by contacting <a href="mailto:support@loopbaan.ai" className="text-primary hover:underline">support@loopbaan.ai</a>.
                    </p>
                  </Card>
                </div>

                {/* Section 4 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">4. User Content and Ownership</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">4.1 Your Content</h3>
                    <p className="mb-4 text-muted-foreground">
                      You retain full ownership of all information you provide to LoopBaan, including:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Professional history and work experience</li>
                      <li>• Educational background</li>
                      <li>• Skills, certifications, and qualifications</li>
                      <li>• Any other personal information you input</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">4.2 License to Use Your Content</h3>
                    <p className="mb-4 text-muted-foreground">
                      By using our Service, you grant LoopBaan a limited, non-exclusive, worldwide, royalty-free license to:
                    </p>
                    <ul className="mb-4 space-y-2 text-muted-foreground">
                      <li>• Process your information using AI to generate resumes and cover letters</li>
                      <li>• Store your content on our secure servers</li>
                      <li>• Display your content back to you within the Service</li>
                      <li>• Use aggregated, anonymized data to improve our AI and Service</li>
                    </ul>
                    <p className="font-semibold text-foreground mb-2">We will NEVER:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Sell your personal information to third parties</li>
                      <li>• Share your resumes or cover letters publicly</li>
                      <li>• Use your specific content to train AI models</li>
                      <li>• Display your information to other users</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">4.3 AI-Generated Content Ownership</h3>
                    <p className="mb-4 text-muted-foreground">
                      All resumes, cover letters, and other documents generated by our AI based on your input are owned by you. You have the right to:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Download and use generated content however you choose</li>
                      <li>• Edit, modify, or delete generated content</li>
                      <li>• Share content with potential employers</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">4.4 User Responsibilities for Content</h3>
                    <p className="mb-4 text-muted-foreground">You are solely responsible for:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• The accuracy and truthfulness of all information you provide</li>
                      <li>• Ensuring your content does not infringe on third-party rights</li>
                      <li>• Reviewing and verifying all AI-generated content before use</li>
                      <li>• How you use generated resumes and cover letters</li>
                    </ul>
                  </Card>
                </div>

                {/* Section 5 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">5. AI-Generated Content and Disclaimers</h2>
                  
                  <Card className="mb-6 p-6 bg-destructive/5 border-destructive/30">
                    <h3 className="mb-3 text-lg font-semibold text-destructive">5.1 AI Limitations and Accuracy</h3>
                    <p className="mb-4 font-semibold text-destructive">
                      IMPORTANT: AI-GENERATED CONTENT MAY CONTAIN ERRORS, INACCURACIES, OR INAPPROPRIATE SUGGESTIONS.
                    </p>
                    <p className="mb-4 text-muted-foreground">
                      Our Service uses advanced artificial intelligence, but AI is not perfect. You acknowledge and agree that:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• AI may generate incorrect or misleading information</li>
                      <li>• Generated content may require editing and verification</li>
                      <li>• AI cannot guarantee grammatical perfection</li>
                      <li>• Output quality depends on input quality</li>
                      <li>• AI may occasionally produce unexpected or irrelevant results</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6 bg-destructive/5 border-destructive/30">
                    <h3 className="mb-3 text-lg font-semibold text-destructive">5.2 Your Responsibility to Review</h3>
                    <p className="mb-4 font-semibold text-destructive">
                      YOU ARE SOLELY RESPONSIBLE FOR REVIEWING, EDITING, AND VERIFYING ALL AI-GENERATED CONTENT BEFORE USE.
                    </p>
                    <p className="mb-4 text-muted-foreground">
                      Before submitting any resume or cover letter to an employer, you must:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Carefully review all content for accuracy</li>
                      <li>• Verify dates, job titles, and company names</li>
                      <li>• Check for grammatical errors and typos</li>
                      <li>• Ensure information is truthful and not misleading</li>
                      <li>• Confirm content is appropriate for your target role</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">5.3 No Professional Advice</h3>
                    <p className="mb-4 text-muted-foreground">
                      LoopBaan is a technology tool, not a career counseling or professional advisory service. We do not provide:
                    </p>
                    <ul className="mb-4 space-y-2 text-muted-foreground">
                      <li>• Career advice or job placement services</li>
                      <li>• Legal advice regarding employment matters</li>
                      <li>• Guarantees or recommendations about career decisions</li>
                    </ul>
                    <p className="text-muted-foreground">
                      Any suggestions or recommendations provided by our AI should not be construed as professional advice.
                    </p>
                  </Card>

                  <Card className="p-6 bg-muted/30">
                    <h3 className="mb-3 text-lg font-semibold">5.4 No Guarantee of Results</h3>
                    <p className="mb-4 font-semibold text-foreground">
                      WE MAKE NO GUARANTEES REGARDING JOB SEARCH OUTCOMES.
                    </p>
                    <p className="mb-4 text-muted-foreground">Using LoopBaan does not guarantee:</p>
                    <ul className="mb-4 space-y-2 text-muted-foreground">
                      <li>• Job interviews or offers</li>
                      <li>• Employment or career advancement</li>
                      <li>• Specific hiring outcomes</li>
                      <li>• Increased response rates from employers</li>
                      <li>• Success in job applications</li>
                    </ul>
                    <p className="text-muted-foreground">
                      Employment outcomes depend on many factors beyond our control, including market conditions, your qualifications, employer preferences, and timing.
                    </p>
                  </Card>
                </div>

                {/* Section 6 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">6. Subscription Plans and Payments</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">6.1 Subscription Tiers</h3>
                    <p className="mb-4 text-muted-foreground">
                      LoopBaan offers multiple subscription options:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong className="text-foreground">Free Tier:</strong> Limited features and usage</li>
                      <li>• <strong className="text-foreground">Premium Monthly:</strong> Full access with monthly billing</li>
                      <li>• <strong className="text-foreground">Premium Annual:</strong> Full access with annual billing at discounted rate</li>
                    </ul>
                    <p className="mt-4 text-sm text-muted-foreground">
                      Specific features, limitations, and pricing are available on our pricing page.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">6.2 Payment Processing</h3>
                    <p className="mb-4 text-muted-foreground">
                      All payments are processed securely through Stripe, a third-party payment processor. By providing payment information, you:
                    </p>
                    <ul className="mb-4 space-y-2 text-muted-foreground">
                      <li>• Authorize us to charge your payment method for applicable fees</li>
                      <li>• Represent that payment information is accurate and you are authorized to use it</li>
                      <li>• Agree to Stripe&apos;s Terms of Service and Privacy Policy</li>
                    </ul>
                    <p className="text-muted-foreground">We do not store your credit card details.</p>
                  </Card>

                  <Card className="mb-6 p-6 bg-primary/5">
                    <h3 className="mb-3 text-lg font-semibold">6.3 Automatic Renewal</h3>
                    <p className="mb-4 font-semibold text-foreground">
                      SUBSCRIPTIONS AUTOMATICALLY RENEW UNLESS CANCELLED.
                    </p>
                    <p className="mb-4 text-muted-foreground">
                      By subscribing, you authorize recurring charges to your payment method:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Monthly subscriptions renew monthly until cancelled</li>
                      <li>• Annual subscriptions renew annually until cancelled</li>
                      <li>• You will be charged on the renewal date unless you cancel beforehand</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">6.4 Price Changes</h3>
                    <p className="mb-4 text-muted-foreground">
                      We reserve the right to change subscription prices at any time. Price changes will:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Be communicated to you at least 30 days in advance via email</li>
                      <li>• Not affect your current billing cycle</li>
                      <li>• Apply to subsequent renewal periods</li>
                      <li>• Provide you the opportunity to cancel before the new price takes effect</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">6.5 Taxes</h3>
                    <p className="text-muted-foreground">
                      Prices do not include applicable taxes, which may be added to your bill based on your location. You are responsible for all sales, use, and similar taxes.
                    </p>
                  </Card>
                </div>

                {/* Section 7 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">7. Refund Policy</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">7.1 30-Day Money-Back Guarantee</h3>
                    <p className="mb-4 text-muted-foreground">
                      We offer a 30-day money-back guarantee for new subscribers:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong className="text-foreground">Eligibility:</strong> First-time subscribers only</li>
                      <li>• <strong className="text-foreground">Timeframe:</strong> Request refund within 30 days of initial purchase</li>
                      <li>• <strong className="text-foreground">Process:</strong> Email support@loopbaan.ai with refund request</li>
                      <li>• <strong className="text-foreground">Refund Method:</strong> Original payment method within 5-7 business days</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">7.2 Refund Exclusions</h3>
                    <p className="mb-4 text-muted-foreground">Refunds are NOT available for:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Renewals after the initial subscription period</li>
                      <li>• Partial refunds for unused subscription time</li>
                      <li>• Accounts terminated for Terms violations</li>
                      <li>• Services already rendered or downloaded content</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">7.3 Free Trial (If Applicable)</h3>
                    <p className="mb-4 text-muted-foreground">If we offer a free trial:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Trial period is specified at signup</li>
                      <li>• Your payment method will be charged automatically when trial ends</li>
                      <li>• Cancel before trial ends to avoid charges</li>
                      <li>• No refunds for forgetting to cancel after trial</li>
                    </ul>
                  </Card>
                </div>

                {/* Section 8 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">8. Acceptable Use Policy</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">8.1 Permitted Uses</h3>
                    <p className="text-muted-foreground">
                      You may use LoopBaan only for lawful purposes to create resumes and cover letters for legitimate job search activities.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">8.2 Prohibited Conduct</h3>
                    <p className="mb-4 text-muted-foreground">You agree NOT to:</p>
                    
                    <p className="mt-4 mb-2 font-semibold text-foreground">Content-Related:</p>
                    <ul className="mb-4 space-y-2 text-muted-foreground">
                      <li>• Upload false, misleading, or fraudulent information</li>
                      <li>• Impersonate another person or misrepresent your identity</li>
                      <li>• Include copyrighted content you don&apos;t have rights to use</li>
                      <li>• Submit offensive, defamatory, or discriminatory content</li>
                    </ul>

                    <p className="mb-2 font-semibold text-foreground">Technical Abuse:</p>
                    <ul className="mb-4 space-y-2 text-muted-foreground">
                      <li>• Attempt to hack, reverse engineer, or circumvent security measures</li>
                      <li>• Use automated tools (bots, scrapers) without permission</li>
                      <li>• Overload or disrupt our servers or infrastructure</li>
                      <li>• Attempt to access other users&apos; accounts or data</li>
                    </ul>

                    <p className="mb-2 font-semibold text-foreground">Commercial Abuse:</p>
                    <ul className="mb-4 space-y-2 text-muted-foreground">
                      <li>• Resell or redistribute our Service without authorization</li>
                      <li>• Use the Service to compete with LoopBaan</li>
                      <li>• Share account credentials with others</li>
                      <li>• Create multiple accounts to bypass limits</li>
                    </ul>

                    <p className="mb-2 font-semibold text-foreground">Illegal Activity:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Use the Service for any illegal purpose</li>
                      <li>• Violate any local, state, national, or international law</li>
                      <li>• Infringe on intellectual property rights</li>
                      <li>• Engage in fraud, phishing, or identity theft</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">8.3 Consequences of Violations</h3>
                    <p className="mb-4 text-muted-foreground">Violations may result in:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Warning and request to cease prohibited activity</li>
                      <li>• Temporary suspension of your account</li>
                      <li>• Permanent termination without refund</li>
                      <li>• Legal action if necessary</li>
                      <li>• Reporting to law enforcement</li>
                    </ul>
                  </Card>
                </div>

                {/* Section 9 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">9. Intellectual Property Rights</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">9.1 LoopBaan&apos;s Intellectual Property</h3>
                    <p className="mb-4 text-muted-foreground">
                      All aspects of the Service are protected by intellectual property laws, including:
                    </p>
                    <ul className="mb-4 space-y-2 text-muted-foreground">
                      <li>• Website design, layout, and graphics</li>
                      <li>• Software code and algorithms</li>
                      <li>• LoopBaan name, logo, and trademarks</li>
                      <li>• AI models and technology (excluding user content)</li>
                    </ul>
                    <p className="mb-2 text-muted-foreground">You may not:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Copy, modify, or distribute our proprietary materials</li>
                      <li>• Use our trademarks without written permission</li>
                      <li>• Remove copyright or proprietary notices</li>
                      <li>• Create derivative works based on our Service</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">9.2 Limited License to Use Service</h3>
                    <p className="text-muted-foreground">
                      We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for personal, non-commercial purposes in accordance with these Terms.
                    </p>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">9.3 Copyright Infringement</h3>
                    <p className="mb-4 text-muted-foreground">
                      We respect intellectual property rights. If you believe content on our Service infringes your copyright, contact us at:
                    </p>
                    <p className="mb-4 text-muted-foreground">
                      <strong className="text-foreground">Email:</strong> <a href="mailto:legal@loopbaan.ai" className="text-primary hover:underline">legal@loopbaan.ai</a><br />
                      <strong className="text-foreground">Subject:</strong> Copyright Infringement Claim
                    </p>
                    <p className="mb-2 text-muted-foreground">Include:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Description of copyrighted work</li>
                      <li>• Location of infringing material</li>
                      <li>• Your contact information</li>
                      <li>• Statement of good faith belief</li>
                      <li>• Statement under penalty of perjury that information is accurate</li>
                    </ul>
                  </Card>
                </div>

                {/* Section 10 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">10. Third-Party Services</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">10.1 Third-Party Integration</h3>
                    <p className="mb-4 text-muted-foreground">
                      Our Service may integrate with or link to third-party services, including:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Anthropic (Claude AI for content generation)</li>
                      <li>• Stripe (payment processing)</li>
                      <li>• Hosting providers (Vercel, AWS)</li>
                      <li>• Analytics services</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">10.2 Third-Party Terms</h3>
                    <p className="mb-4 text-muted-foreground">
                      Your use of third-party services is governed by their respective terms and privacy policies. We are not responsible for:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Third-party service availability or reliability</li>
                      <li>• Third-party data practices</li>
                      <li>• Third-party terms or policy changes</li>
                      <li>• Issues arising from third-party services</li>
                    </ul>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">10.3 External Links</h3>
                    <p className="text-muted-foreground">
                      Our Service may contain links to external websites. We do not endorse or assume responsibility for third-party content, products, or services.
                    </p>
                  </Card>
                </div>

                {/* Section 11 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">11. Disclaimers and Limitations of Liability</h2>
                  
                  <Card className="mb-6 p-6 bg-muted/30">
                    <h3 className="mb-3 text-lg font-semibold">11.1 Service &quot;As Is&quot;</h3>
                    <p className="mb-4 font-semibold text-foreground">
                      THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND.
                    </p>
                    <p className="mb-4 text-muted-foreground">
                      We disclaim all warranties, express or implied, including:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Warranties of merchantability or fitness for a particular purpose</li>
                      <li>• Warranties of non-infringement</li>
                      <li>• Warranties of uninterrupted or error-free service</li>
                      <li>• Warranties regarding accuracy or reliability of AI-generated content</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">11.2 No Guarantee of Results</h3>
                    <p className="mb-4 text-muted-foreground">We make no guarantees about:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• The quality of AI-generated content</li>
                      <li>• Employment outcomes or job search success</li>
                      <li>• Compatibility with all job application systems</li>
                      <li>• Meeting your specific needs or expectations</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6 bg-destructive/5 border-destructive/30">
                    <h3 className="mb-3 text-lg font-semibold text-destructive">11.3 Limitation of Liability</h3>
                    <p className="mb-4 font-semibold text-destructive">
                      TO THE MAXIMUM EXTENT PERMITTED BY LAW, LOOPBAAN SHALL NOT BE LIABLE FOR:
                    </p>
                    <ul className="mb-4 space-y-2 text-muted-foreground">
                      <li>• Indirect, incidental, special, consequential, or punitive damages</li>
                      <li>• Loss of profits, revenue, data, or business opportunities</li>
                      <li>• Emotional distress or reputational harm</li>
                      <li>• Damages resulting from AI errors or inaccuracies</li>
                      <li>• Damages from unauthorized access to your account</li>
                      <li>• Damages from service interruptions or downtime</li>
                    </ul>
                    <p className="font-semibold text-foreground">TOTAL LIABILITY CAP:</p>
                    <p className="text-muted-foreground">
                      Our total liability for all claims shall not exceed the amount you paid us in the 12 months preceding the claim, or $100, whichever is greater.
                    </p>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">11.4 Limitations by Jurisdiction</h3>
                    <p className="text-muted-foreground">
                      Some jurisdictions do not allow limitation of liability for certain damages. In such cases, our liability is limited to the extent permitted by law.
                    </p>
                  </Card>
                </div>

                {/* Section 12 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">12. Indemnification</h2>
                  <Card className="p-6">
                    <p className="mb-4 text-muted-foreground">
                      You agree to indemnify, defend, and hold harmless LoopBaan, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including attorney fees) arising from:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Your use or misuse of the Service</li>
                      <li>• Your violation of these Terms</li>
                      <li>• Your violation of any third-party rights</li>
                      <li>• Content you submit or create using the Service</li>
                      <li>• Your representations to employers based on AI-generated content</li>
                      <li>• Any fraudulent or illegal activity related to your account</li>
                    </ul>
                  </Card>
                </div>

                {/* Section 13 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">13. Dispute Resolution</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">13.1 Governing Law</h3>
                    <p className="text-muted-foreground">
                      These Terms are governed by the laws of the State of Missouri, United States, without regard to conflict of law principles.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">13.2 Informal Resolution</h3>
                    <p className="text-muted-foreground">
                      Before filing a claim, you agree to contact us at <a href="mailto:legal@loopbaan.ai" className="text-primary hover:underline">legal@loopbaan.ai</a> to attempt informal resolution. We will work in good faith to resolve disputes within 30 days.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6 bg-primary/5">
                    <h3 className="mb-3 text-lg font-semibold">13.3 Binding Arbitration</h3>
                    <p className="mb-4 font-semibold text-foreground">
                      BY AGREEING TO THESE TERMS, YOU AGREE TO RESOLVE DISPUTES THROUGH BINDING ARBITRATION, NOT COURT.
                    </p>
                    <p className="mb-4 text-muted-foreground">
                      If informal resolution fails, any dispute shall be resolved by binding arbitration administered by the American Arbitration Association (AAA) under its Commercial Arbitration Rules.
                    </p>
                    <p className="mb-2 font-semibold text-foreground">Arbitration Terms:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Individual arbitration only (no class actions)</li>
                      <li>• Location: Kansas City, Missouri (or virtual)</li>
                      <li>• Costs: Each party pays their own costs unless otherwise awarded</li>
                      <li>• Award: Arbitrator&apos;s decision is final and binding</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">13.4 Exceptions to Arbitration</h3>
                    <p className="text-muted-foreground">
                      You may bring claims in small claims court if they qualify. Either party may seek injunctive relief in court for intellectual property disputes.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6 bg-muted/30">
                    <h3 className="mb-3 text-lg font-semibold">13.5 Class Action Waiver</h3>
                    <p className="mb-4 font-semibold text-foreground">
                      YOU AGREE TO BRING CLAIMS ONLY IN YOUR INDIVIDUAL CAPACITY, NOT AS PART OF A CLASS ACTION.
                    </p>
                    <p className="text-muted-foreground">
                      You waive any right to participate in class actions, consolidated proceedings, or representative actions.
                    </p>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">13.6 Venue</h3>
                    <p className="text-muted-foreground">
                      If arbitration does not apply, exclusive jurisdiction and venue shall be the state and federal courts located in Jackson County, Missouri.
                    </p>
                  </Card>
                </div>

                {/* Section 14 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">14. Privacy and Data Protection</h2>
                  <Card className="p-6">
                    <p className="mb-4 text-muted-foreground">
                      Your use of the Service is subject to our Privacy Policy, which is incorporated by reference into these Terms. By using LoopBaan, you consent to our collection, use, and disclosure of your information as described in our Privacy Policy.
                    </p>
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">Read our full Privacy Policy at:</strong> <Link to="/privacy" className="text-primary hover:underline">loopbaan.ai/privacy</Link>
                    </p>
                  </Card>
                </div>

                {/* Section 15 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">15. Communications and Notices</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">15.1 Electronic Communications</h3>
                    <p className="mb-4 text-muted-foreground">
                      By using the Service, you consent to receive communications from us electronically, including:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• Transactional emails (receipts, password resets, account notifications)</li>
                      <li>• Service updates and announcements</li>
                      <li>• Marketing emails (if you opt in)</li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">15.2 How to Contact Us</h3>
                    <p className="mb-4 text-muted-foreground">
                      For questions, support, or notices to LoopBaan:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Email:</strong> <a href="mailto:support@loopbaan.ai" className="text-primary hover:underline">support@loopbaan.ai</a></li>
                    </ul>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">15.3 How We Contact You</h3>
                    <p className="text-muted-foreground">
                      We will contact you at the email address associated with your account. You agree to keep this information current.
                    </p>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">15.4 Opt-Out of Marketing</h3>
                    <p className="text-muted-foreground">
                      You may unsubscribe from marketing emails using the link in any promotional email. This does not affect transactional emails necessary for the Service.
                    </p>
                  </Card>
                </div>

                {/* Section 16 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">16. General Provisions</h2>
                  
                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">16.1 Entire Agreement</h3>
                    <p className="text-muted-foreground">
                      These Terms, together with our Privacy Policy, constitute the entire agreement between you and LoopBaan regarding use of the Service.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">16.2 Severability</h3>
                    <p className="text-muted-foreground">
                      If any provision of these Terms is found invalid or unenforceable, the remaining provisions remain in full force and effect.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">16.3 Waiver</h3>
                    <p className="text-muted-foreground">
                      Our failure to enforce any right or provision of these Terms does not constitute a waiver of that right or provision.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">16.4 Assignment</h3>
                    <p className="text-muted-foreground">
                      You may not assign or transfer your rights under these Terms. We may assign our rights to any affiliate, successor, or acquirer.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">16.5 Survival</h3>
                    <p className="text-muted-foreground">
                      Provisions that by their nature should survive termination shall survive, including: ownership, warranty disclaimers, indemnification, limitations of liability, and dispute resolution.
                    </p>
                  </Card>

                  <Card className="mb-6 p-6">
                    <h3 className="mb-3 text-lg font-semibold">16.6 Force Majeure</h3>
                    <p className="text-muted-foreground">
                      We are not liable for delays or failures caused by circumstances beyond our reasonable control, including natural disasters, war, terrorism, pandemics, or government actions.
                    </p>
                  </Card>

                  <Card className="p-6">
                    <h3 className="mb-3 text-lg font-semibold">16.7 Independent Contractors</h3>
                    <p className="text-muted-foreground">
                      Nothing in these Terms creates a partnership, joint venture, agency, or employment relationship between you and LoopBaan.
                    </p>
                  </Card>
                </div>

                {/* Section 17 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">17. Feedback and Suggestions</h2>
                  <Card className="p-6">
                    <p className="text-muted-foreground">
                      If you provide feedback, ideas, or suggestions about the Service (&quot;Feedback&quot;), you grant us a perpetual, irrevocable, worldwide, royalty-free license to use, modify, and incorporate such Feedback without compensation or attribution to you.
                    </p>
                  </Card>
                </div>

                {/* Section 18 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">18. User Testimonials and Reviews</h2>
                  <Card className="p-6">
                    <p className="text-muted-foreground">
                      If you provide testimonials, reviews, or endorsements about LoopBaan, you grant us permission to use such statements in our marketing materials, website, and promotional content, along with your name (unless you request anonymity).
                    </p>
                  </Card>
                </div>

                {/* Section 19 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">19. Export Compliance</h2>
                  <Card className="p-6">
                    <p className="text-muted-foreground">
                      You agree to comply with all applicable export and import laws and regulations. You may not use the Service if you are located in a country subject to U.S. embargo or if you are on any U.S. government list of prohibited or restricted parties.
                    </p>
                  </Card>
                </div>

                {/* Section 20 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">20. Acknowledgment and Acceptance</h2>
                  <Card className="p-6 bg-primary/5 border-primary/30">
                    <p className="mb-4 font-semibold text-foreground">
                      BY CLICKING &quot;I AGREE,&quot; CREATING AN ACCOUNT, OR USING THE SERVICE, YOU ACKNOWLEDGE THAT:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• You have read and understood these Terms</li>
                      <li>• You agree to be bound by these Terms</li>
                      <li>• You are at least 18 years of age</li>
                      <li>• You have the authority to enter into this agreement</li>
                      <li>• You understand the AI disclaimers and limitations</li>
                      <li>• You will review all AI-generated content before use</li>
                    </ul>
                    <p className="mt-4 font-semibold text-foreground">
                      IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE THE SERVICE.
                    </p>
                  </Card>
                </div>

                {/* Section 21 */}
                <div>
                  <h2 className="mb-4 text-2xl font-bold">21. Questions and Contact Information</h2>
                  <Card className="bg-primary/5 p-6">
                    <p className="mb-4 text-muted-foreground">
                      If you have questions about these Terms:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li><strong className="text-foreground">Email:</strong> <a href="mailto:legal@loopbaan.ai" className="text-primary hover:underline">legal@loopbaan.ai</a></li>
                      <li><strong className="text-foreground">Support:</strong> <a href="mailto:support@loopbaan.ai" className="text-primary hover:underline">support@loopbaan.ai</a></li>
                    </ul>
                    <p className="mt-4 text-sm text-muted-foreground">
                      We will respond to inquiries within 5 business days.
                    </p>
                  </Card>
                </div>

                <div className="mt-12 text-center">
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-foreground">LoopBaan.ai LLC</strong><br />
                    Last Updated: October 25, 2025 | Effective Date: October 25, 2025<br />
                    © 2025 LoopBaan.ai LLC. All rights reserved.
                  </p>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t bg-muted/30 py-12">
          <div className="container mx-auto px-4">
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
              <Link to="/" className="flex items-center gap-2">
                <Logo className="h-12 w-12" />
                <span className="font-semibold">LoopBaan.ai</span>
              </Link>
              <div className="flex gap-6 text-sm text-muted-foreground">
                <Link to="/contact" className="hover:text-foreground">Contact</Link>
                <Link to="/ats-optimization" className="hover:text-foreground">ATS Guide</Link>
                <Link to="/terms" className="hover:text-foreground">Terms</Link>
                <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
              </div>
            </div>
            <div className="mt-8 text-center text-sm text-muted-foreground">
              © 2025 LoopBaan.ai LLC. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Terms;